import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  getCachedCards,
  identifyBrazeUser,
  logCardClick,
  logCardImpression,
  logCardImpressions,
  refreshContentCards,
  resetBrazeUser,
  startBraze,
  wipeBrazeData,
} from "../services/braze/braze.service";
import { BrazeContentCard, isActiveCard } from "../services/braze/braze.types";

import { useUserStore } from "../hooks/redux-hooks/user-store/user.store";

interface BrazeContextValue {
  cards: BrazeContentCard[];
  unviewedCount: number;
  logImpression: (card: BrazeContentCard) => void;
  logClick: (card: BrazeContentCard) => void;
  markAllViewed: () => void;
}

const BrazeContext = createContext<BrazeContextValue>({
  cards: [],
  unviewedCount: 0,
  logImpression: () => undefined,
  logClick: () => undefined,
  markAllViewed: () => undefined,
});
BrazeContext.displayName = "BrazeContext";

export const BrazeProvider: FC = ({ children }) => {
  const { profileUser } = useUserStore();
  const [cards, setCards] = useState<BrazeContentCard[]>([]);
  const loggedImpressions = useRef<Set<string>>(new Set());

  // Braze External ID must be the Mongo user id, which `getProfile` exposes as
  // `objectUserId`. It has to match the identifier the backend uses when it
  // syncs users to Braze so content cards target the same profile. Do NOT use
  // `id` (that field is the Auth0 id) or `contact_id`/`email`.
  const externalId = profileUser?.objectUserId ?? null;

  const handleCards = useCallback((next: BrazeContentCard[]) => {
    // Ordering is applied downstream by `sortFeedEntries` on the merged feed,
    // which is the single ordering authority; only filter here.
    setCards(next.filter(isActiveCard));
  }, []);

  useEffect(() => {
    // Content Cards only matter once the user is logged in, so don't start a
    // Braze session (which would open an anonymous session and fetch cards)
    // until we have an external id.
    if (!externalId) {
      // Logged out: reset the identity guard so a returning user (even the
      // same id) gets re-identified on the next login, wipe the previous
      // user's locally cached SDK data, and drop their cards from the UI.
      resetBrazeUser();
      wipeBrazeData();
      loggedImpressions.current.clear();
      setCards([]);

      return;
    }

    let active = true;

    startBraze(handleCards, externalId).then((ready) => {
      if (!active || !ready) {
        return;
      }
      // Re-identify if the user logged in after the session already started.
      identifyBrazeUser(externalId);
      handleCards(getCachedCards());
    });

    return () => {
      active = false;
    };
  }, [handleCards, externalId]);

  useEffect(() => {
    // Only refresh for a known user, and use a single listener: `focus` and
    // `visibilitychange` both fire on tab return, so one avoids double syncs.
    if (!externalId) {
      return;
    }

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refreshContentCards();
      }
    };

    document.addEventListener("visibilitychange", onVisible);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [externalId]);

  const logImpression = useCallback((card: BrazeContentCard) => {
    // Also skip cards the SDK already reports as viewed so a logout + re-login
    // cycle doesn't re-fire impressions for already-seen cards.
    if (!card.id || card.viewed || loggedImpressions.current.has(card.id)) {
      return;
    }
    loggedImpressions.current.add(card.id);
    logCardImpression(card);
    // Optimistically mark viewed so the notification badge updates immediately
    // rather than waiting for the next content-cards update from the SDK.
    setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, viewed: true } : c)));
  }, []);

  const logClick = useCallback((card: BrazeContentCard) => logCardClick(card), []);

  // "Mark all as read" needs to clear content cards too,
  // otherwise below-the-fold
  // cards (only viewed via the per-card IntersectionObserver) keep the badge
  // non-zero. Log impressions for the not-yet-logged, unviewed cards, then mark
  // every card viewed so the badge drops to zero.
  const markAllViewed = useCallback(() => {
    setCards((prev) => {
      const toLog = prev.filter(
        (card) => card.id && !card.viewed && !loggedImpressions.current.has(card.id),
      );

      toLog.forEach((card) => loggedImpressions.current.add(card.id));
      logCardImpressions(toLog);

      return prev.map((card) => (card.viewed ? card : { ...card, viewed: true }));
    });
  }, []);

  // Badge counts only unviewed cards already rendered in the feed.
  const unviewedCount = useMemo(
    () => cards.filter((card) => !card.viewed).length,
    [cards],
  );

  const value = useMemo<BrazeContextValue>(
    () => ({ cards, unviewedCount, logImpression, logClick, markAllViewed }),
    [cards, unviewedCount, logImpression, logClick, markAllViewed],
  );

  return <BrazeContext.Provider value={value}>{children}</BrazeContext.Provider>;
};

export const useBraze = (): BrazeContextValue => useContext(BrazeContext);
